// src/pages/UnsubscribePage.tsx
import React, { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { ArrowLeft, Check, AlertCircle } from 'lucide-react'

const UnsubscribePage = () => {
  const [loading, setLoading] = useState(false)
  const [unsubscribed, setUnsubscribed] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [email, setEmail] = useState('')
  
  const location = useLocation()
  
  // Get email from query parameters
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const emailParam = params.get('email')
    if (emailParam) {
      setEmail(emailParam)
    }
  }, [location])
  
  const handleUnsubscribe = async () => {
    if (!email) {
      setError('Email is required')
      return
    }
    
    setLoading(true)
    setError(null)
    
    try {
      const response = await fetch('/.netlify/functions/handle-unsubscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          email, 
          subscribe: false 
        })
      })
      
      const data = await response.json()
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to unsubscribe')
      }
      
      // Show success message
      setUnsubscribed(true)
    } catch (err: any) {
      console.error('❌ Unsubscribe error:', err)
      setError(err.message || 'Failed to unsubscribe')
    } finally {
      setLoading(false)
    }
  }
  
  const handleResubscribe = async () => {
    setLoading(true)
    setError(null)
    
    try {
      const response = await fetch('/.netlify/functions/handle-unsubscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          email, 
          subscribe: true 
        })
      })
      
      const data = await response.json()
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to resubscribe')
      }
      
      setUnsubscribed(false)
    } catch (err: any) {
      console.error('❌ Resubscribe error:', err)
      setError(err.message || 'Failed to resubscribe')
    } finally {
      setLoading(false)
    }
  }
  
  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center p-4 relative overflow-hidden">
      {/* Atmospheric lighting effects */}
      <div className="absolute top-0 right-0 w-[800px] h-[800px] rounded-full bg-primary-500/15 blur-[120px] -z-10"></div>
      <div className="absolute bottom-0 left-0 w-[800px] h-[800px] rounded-full bg-gray-300/20 blur-[120px] -z-10"></div>
      
      <div className="w-full max-w-md mb-6 relative z-10">
        <Button as={Link} to="/" variant="ghost" className="flex items-center text-gray-700 hover:text-gray-950 transition-colors">
          <ArrowLeft size={18} className="mr-1" />
          Back to Home
        </Button>
      </div>
      
      <Card className="w-full max-w-md glass-card shadow-lg z-10">
        <CardHeader>
          <CardTitle className="text-center text-2xl font-display font-bold">
            Email <span className="gradient-text">Preferences</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 flex items-center">
              <AlertCircle size={18} className="mr-2" />
              {error}
            </div>
          )}
          
          {unsubscribed ? (
            <div className="text-center">
              <div className="flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mx-auto mb-4">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Successfully Unsubscribed</h3>
              <p className="text-gray-600 mb-6">
                You've been unsubscribed from marketing emails from Simplified Trading Journal. 
                You'll still receive important account notifications.
              </p>
              
              <Button
                type="button"
                onClick={handleResubscribe}
                className={`py-3 text-lg font-medium shadow-md hover:shadow-lg transition-all duration-300 !rounded-full ${
                  loading
                    ? 'bg-gray-400 text-white cursor-not-allowed'
                    : 'bg-primary-700 hover:bg-primary-800 text-white'
                }`}
                disabled={loading}
              >
                {loading ? 'Processing...' : 'Resubscribe'}
              </Button>
              
              <div className="mt-4">
                <Link to="/" className="text-primary-700 hover:text-primary-600 transition-colors">
                  Return to home page
                </Link>
              </div>
            </div>
          ) : (
            <div>
              <p className="mb-6 text-gray-700">
                You're about to unsubscribe <span className="font-semibold">{email}</span> from marketing emails. 
                You'll still receive important account notifications.
              </p>
              
              <Button
                type="button"
                onClick={handleUnsubscribe}
                fullWidth
                className={`py-3 text-lg font-medium shadow-md hover:shadow-lg transition-all duration-300 !rounded-full ${
                  loading
                    ? 'bg-gray-400 text-white cursor-not-allowed'
                    : 'bg-black hover:bg-gray-900 text-white'
                }`}
                disabled={loading}
              >
                {loading ? 'Processing...' : 'Confirm Unsubscribe'}
              </Button>
              
              <div className="text-center text-sm text-gray-700 mt-4">
                Changed your mind?{' '}
                <Link to="/" className="text-primary-700 hover:text-primary-600 transition-colors hover:underline">
                  Return to home
                </Link>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export default UnsubscribePage